import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Send, Loader, Download, CheckCircle, X, AlertCircle } from 'lucide-react';
import API_URL from '../config';
import logger from '../utils/logger';


const AskYourCoach = ({ onComplete }) => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  const [saved, setSaved] = useState(false);
  const conversationEndRef = useRef(null);

  useEffect(() => {
    // Scroll to bottom of conversation when it updates
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  const handleQuestionChange = (e) => {
    setQuestion(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    setLoading(true);
    setError('');
    
    // Add user question to conversation
    setConversation(prev => [...prev, { role: 'user', content: question }]);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await axios.post(
        `${API_URL}/api/ai/ask-coach`,
        { question },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.answer) {
        // Add coach's response to conversation
        setConversation(prev => [...prev, { role: 'coach', content: response.data.answer }]);
        setQuestion(''); // Clear the input field
        setSuccessMessage(''); // Clear any previous success messages
        setSaved(false); // Reset saved state
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      logger.error('Error getting coach response:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred. Please try again.';
      setError(errorMessage);
      
      // If there was an error in the response, still show it in the conversation
      if (error.response?.data?.DEV) {
        setConversation(prev => [...prev, { 
          role: 'coach', 
          content: 'I apologize, but I encountered an issue while processing your request. Please try rephrasing your question or try again later.' 
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConversation = async () => {
    if (conversation.length === 0) {
      setError('No conversation to save');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      await axios.post(
        `${API_URL}/api/ai/save-conversation`,
        { conversation },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage('Conversation saved successfully!');
      setSaved(true);
    } catch (error) {
      logger.error('Error saving conversation:', error);
      setError('Failed to save conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadConversation = () => {
    if (conversation.length === 0) {
      setError('No conversation to download');
      return;
    }

    // Format conversation for text file
    let content = "# Conversation with Mental Edge Coach\n\n";
    conversation.forEach(msg => {
      content += `${msg.role === 'user' ? 'YOU' : 'COACH'}: ${msg.content}\n\n`;
    });

    // Create and download the file
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `coach-conversation-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const clearConversation = () => {
    setConversation([]);
    setSaved(false);
  };

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6">Ask Your Mental Edge Coach</h1>
      
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative flex items-center">
          <AlertCircle className="mr-2" size={20} />
          <span>{error}</span>
          <button 
            className="absolute top-0 right-0 p-2" 
            onClick={() => setError('')}
          >
            <X size={20} />
          </button>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative flex items-center">
          <CheckCircle className="mr-2" size={20} />
          <span>{successMessage}</span>
          <button 
            className="absolute top-0 right-0 p-2" 
            onClick={() => setSuccessMessage('')}
          >
            <X size={20} />
          </button>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="mb-4">
          <p className="text-gray-700">
            Ask your Mental Edge Coach for personalized advice on mental performance techniques, 
            overcoming challenges, setting goals, or improving your mindset for better results.
          </p>
        </div>
        
        {/* Conversation Area */}
        {conversation.length > 0 && (
          <div className="mb-6">
            <div className="border rounded-lg p-4 h-80 overflow-y-auto bg-gray-50">
              {conversation.map((msg, index) => (
                <div 
                  key={index} 
                  className={`mb-4 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}
                >
                  <div 
                    className={`inline-block p-3 rounded-lg max-w-3/4 ${
                      msg.role === 'user' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-200 text-gray-800'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              <div ref={conversationEndRef} />
            </div>
            
            <div className="flex mt-2 space-x-2">
              <button
                onClick={handleSaveConversation}
                disabled={loading || saved}
                className={`flex items-center justify-center px-4 py-2 rounded ${
                  saved 
                    ? 'bg-green-100 text-green-800 cursor-not-allowed' 
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                {saved ? (
                  <CheckCircle className="mr-2" size={18} />
                ) : null}
                {saved ? 'Saved' : 'Save'}
              </button>
              
              <button
                onClick={handleDownloadConversation}
                className="flex items-center justify-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded"
              >
                <Download className="mr-2" size={18} />
                Download
              </button>
              
              <button
                onClick={clearConversation}
                className="flex items-center justify-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded"
              >
                <X className="mr-2" size={18} />
                Clear
              </button>
            </div>
          </div>
        )}
        
        {/* Question Input Form */}
        <form onSubmit={handleSubmit} className="mt-4">
          <div className="relative">
            <textarea
              value={question}
              onChange={handleQuestionChange}
              placeholder="Type your question here..."
              className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 h-24 resize-none"
              disabled={loading}
            ></textarea>
            <button
              type="submit"
              disabled={loading || !question.trim()}
              className={`absolute right-3 bottom-3 p-2 rounded-full ${
                loading || !question.trim() 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {loading ? <Loader className="animate-spin" size={20} /> : <Send size={20} />}
            </button>
          </div>
        </form>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Tips for Asking Good Questions</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>Be specific about the mental challenge you're facing</li>
          <li>Mention your sport or activity for more tailored advice</li>
          <li>Share what you've already tried and what hasn't worked</li>
          <li>Ask about specific techniques rather than general advice</li>
          <li>Provide context about when the issue occurs (practice, competition, etc.)</li>
        </ul>
      </div>
    </div>
  );
};

export default AskYourCoach;